import { useMemo, useState } from "react";
import { TaskStatusType, TaskType, VersionType, WorkflowTaskReferenceKeyType } from "../../../__generated__/generated_types";
import { useHistory } from "react-router-dom";
import { getExitWorkflowRoute } from "../userWorkflowUtils";
import {toast} from "react-toastify";


export type TUserWorkflowTaskStatus = {
    userId: string,
    createdAt: string,
    status: TaskStatusType,
    propertyWorkflowId: string,
}

export type TUserWorkflowTask = {
    id: string,
    accountId?: string | null,
    referenceKey?: WorkflowTaskReferenceKeyType | null,
    title: string,
    instructions?: string | null,
    position: number,
    taskType: TaskType,
    currentStatus?: TUserWorkflowTaskStatus | null,
    statuses?: TUserWorkflowTaskStatus[] | null,
    versionType: VersionType,
}

export type TUserWorkflow = {
    title: string,
    description: string,
    tasks: TUserWorkflowTask[],
}

interface TUserWorkflowWithTasks extends TUserWorkflow {
    tasks: TUserWorkflowTask[],
}

interface IUserWorkflowState {
    propertyWorkflows: TUserWorkflow[];
    setPropertyWorkflows: (workflows: TUserWorkflow[]) => void;
    enterWorkflow: (workflow: TUserWorkflow, taskId: string | null) => void;
    exitWorkflow: () => void;

    activeWorkflow: TUserWorkflowWithTasks | null;
    setActiveWorkflow: (workflow: TUserWorkflow) => void;
    activeTask: TUserWorkflowTask | null;
    setActiveTaskId: (taskId: string) => void;

    goToTask: (task: TUserWorkflowTask) => void;
    nextTask: () => void;
    previousTask: () => void;

    updateTaskStatus: (taskId: string, newStatus: TUserWorkflowTaskStatus) => void;
}

export function useUserWorkflow(): IUserWorkflowState {
    const history = useHistory();

    const [propertyWorkflows, setPropertyWorkflows] = useState<TUserWorkflow[]>([]);
    const [activeWorkflow, setActiveWorkflow] = useState<TUserWorkflow | null>(null);
    const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

    const activeTask = useMemo(() => {
        return activeWorkflow?.tasks.find(task => task.id === activeTaskId) ?? null;
    }, [activeWorkflow, activeTaskId]);

    function enterWorkflow(workflow: TUserWorkflow, taskId: string | null) {
        setActiveWorkflow(workflow);

        const requestedTask = workflow.tasks.find(task => task.id == taskId);
        const firstUnfinishedTask = workflow.tasks.find(task => !task.currentStatus || task.currentStatus.status == TaskStatusType.Pending);
        const firstTask = workflow.tasks[0];

        if (requestedTask) {
            goToTask(requestedTask);
        } else if (firstUnfinishedTask) {
            goToTask(firstUnfinishedTask);
        } else if (firstTask) {
            goToTask(firstTask);
        } else {
            // @bowman - we shouldnt get here, because we dont display workflows with no tasks in the UI, but in case they do theyll get this alert
            toast.error("Workflow has no tasks.");
        }
    }

    function exitWorkflow() {
        const refKey = activeTask?.accountId ?? activeTask?.referenceKey;

        if (activeTask && refKey) {
            const newRoute = getExitWorkflowRoute(activeTask);
            history.push(newRoute);
        }

        setActiveWorkflow(null);
        setActiveTaskId(null);
    }

    function goToTask(task: TUserWorkflowTask, versionType?: string | null) {
        if (!task) {
            return;
        }

        setActiveTaskId(task.id);

        let taskVersionType = task.versionType == VersionType.Reforecast ? "reforecast" : "budget";

        if (versionType) {
            taskVersionType = versionType;
        }

        history.push(`/workflow/${task.id}/${taskVersionType}`);
    }

    function nextTask() {
        if (activeTask && activeWorkflow?.tasks) {
            const nextTask = activeWorkflow.tasks.find(x => x.position == (activeTask.position + 1));

            if (nextTask) {
                goToTask(nextTask);
            }
        }
    }

    function previousTask() {
        if (activeTask && activeWorkflow?.tasks) {
            const previousTask = activeWorkflow.tasks.find(x => x.position == (activeTask.position - 1));

            if (previousTask) {
                goToTask(previousTask);
            }
        }
    }

    function updateTaskStatus(taskId: string, newStatus: TUserWorkflowTaskStatus) {
        setActiveWorkflow(prevWorkflow => {
            if (prevWorkflow) {
                return {
                    ...prevWorkflow,
                    tasks: prevWorkflow.tasks.map(task => task.id == taskId ? {
                        ...task,
                        currentStatus: newStatus,
                        statuses: [newStatus, ...(task?.statuses ?? [])],
                    } : task),
                };
            }
            return null;
        });
    }

    return {
        propertyWorkflows,
        setPropertyWorkflows,
        setActiveWorkflow,
        activeWorkflow,
        activeTask,
        enterWorkflow,
        exitWorkflow,
        goToTask,
        nextTask,
        previousTask,
        setActiveTaskId,
        updateTaskStatus,
    };
}
